import { Logo } from '@syncanix/ui';

import { useT } from '@/core/I18nProvider';

import { WIDGET, WIDGET_TURNS } from './story-data';
import { CORNERS, WidgetComposer, WidgetHeader } from './widget-parts';

import type { CSSProperties, ReactElement } from 'react';

/**
 * The central widget — the thing that assembles itself.
 *
 * Its chrome is reconciled against the REAL shipped widget (`apps/widget`)
 * rather than against a marketing mock: the light brand-plate avatar with the
 * presence dot, bubble-less assistant turns (avatar gutter, author line, then
 * prose), the gradient user bubble at 13px/5px radii, pill tool chips, and the
 * bordered composer. That fidelity is the point — a visitor who installs the
 * product should recognise this exact surface, so the demo is a promise the
 * product keeps rather than an illustration of one.
 *
 * The build order is deliberate and reads as construction: wireframe → corner
 * brackets → surface fill → header → scan sweep → composer → transcript.
 */
export function StoryWidget(): ReactElement {
  const { t } = useT();

  return (
    <div
      data-story-widget
      className="absolute"
      style={{
        insetInlineStart: WIDGET.x,
        top: WIDGET.y,
        width: WIDGET.w,
        height: WIDGET.h,
        animation: 'sxDrift1 12s ease-in-out 12.6s infinite',
      }}
    >
      {/* The wireframe the surface fills in behind, then discards. */}
      <svg
        data-story-deco
        width={WIDGET.w}
        height={WIDGET.h}
        viewBox={`0 0 ${WIDGET.w} ${WIDGET.h}`}
        fill="none"
        aria-hidden="true"
        className="absolute inset-0 overflow-visible"
      >
        <rect
          data-line
          x="1"
          y="1"
          width={WIDGET.w - 2}
          height={WIDGET.h - 2}
          rx="18"
          stroke="url(#sxGa)"
          strokeWidth="1.5"
          fill="none"
          style={
            {
              // A fallback length for the rounded rect's perimeter. `useStoryStage`
              // overwrites it with the measured `getTotalLength()`; this value only
              // matters where measurement throws (jsdom), and being close keeps the
              // draw rate sane there rather than instant.
              '--len': 1622,
              strokeDasharray: 'var(--len)',
              animation:
                'sxDraw 1.05s cubic-bezier(.4,0,.2,1) .12s backwards, sxOut .7s ease 2.4s forwards',
            } as CSSProperties
          }
        />
      </svg>

      {CORNERS.map((corner) => (
        <span key={corner.key} aria-hidden="true" className="absolute" style={corner.style} />
      ))}

      {/* The surface. */}
      <div
        className="absolute inset-0 flex flex-col overflow-hidden rounded-[18px] border border-[rgba(17,24,39,.12)] bg-white text-[#111827]"
        style={{
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          boxShadow:
            '0 40px 80px -28px rgba(4,7,20,.96), 0 12px 30px -12px rgba(4,7,20,.7),' +
            '0 0 0 1px rgba(255,255,255,.16), 0 0 64px -14px rgba(96,132,255,.4),' +
            'inset 0 1px 0 rgba(255,255,255,.9)',
          animation: 'sxFill .65s cubic-bezier(.4,0,.2,1) .85s backwards',
        }}
      >
        <WidgetHeader />

        <div className="relative flex-1 overflow-hidden p-[13px]">
          <ul
            data-feed
            className="m-0 flex list-none flex-col gap-4 p-0"
            style={{ animation: 'sxFeed 17s cubic-bezier(.16,1,.3,1) both' }}
          >
            {WIDGET_TURNS.map((turn) =>
              turn.side === 'user' ? (
                <li
                  key={turn.id}
                  className="max-w-[86%] self-end rounded-[13px_13px_5px_13px] px-[11px] py-[7px] text-sm leading-normal text-white"
                  style={{
                    background: 'linear-gradient(155deg,#2563eb,#1d4ed8)',
                    boxShadow: '0 4px 14px -6px rgba(37,99,235,.38)',
                    animation: `sxUp .45s cubic-bezier(.34,1.56,.64,1) ${turn.at}s backwards`,
                  }}
                >
                  {t(turn.textKey)}
                </li>
              ) : (
                <li
                  key={turn.id}
                  className="flex gap-2.5 self-stretch"
                  style={{
                    animation: `sxUp .55s cubic-bezier(.16,1,.3,1) ${turn.at}s backwards`,
                  }}
                >
                  <span
                    className="mt-px inline-flex size-[26px] flex-none items-center justify-center rounded-full bg-[#f3f4f6]"
                    style={{ boxShadow: '0 0 0 1px #e7eaef, 0 2px 8px -3px rgba(37,99,235,.38)' }}
                  >
                    <Logo size={21} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <span className="text-[11.5px] font-medium leading-tight text-[#5f6673]">
                      {t('home.story.widget.author')}
                    </span>
                    {turn.toolKey ? (
                      <span className="inline-flex flex-none items-center gap-1 self-start whitespace-nowrap rounded-full border border-[rgba(17,24,39,.12)] bg-[#f9fafc] py-0.5 pe-2 ps-1.5 text-xs leading-tight text-[#5f6673]">
                        <span
                          aria-hidden="true"
                          className="grid size-[13px] flex-none place-items-center rounded-full bg-[#047857] text-[8px] text-white"
                        >
                          ✓
                        </span>
                        {t(turn.toolKey)}
                        <span className="text-[#5f6673]">·</span>
                        {turn.toolMetaKey ? t(turn.toolMetaKey) : null}
                      </span>
                    ) : null}
                    <p
                      className="m-0 text-sm leading-normal text-[#111827]"
                      style={{ animation: `sxWipe .9s ease ${turn.at + 0.15}s backwards` }}
                    >
                      {t(turn.textKey)}
                    </p>
                  </span>
                </li>
              ),
            )}
          </ul>

          {/* The scan sweeping down as it reads the product. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-[66px] opacity-0"
            style={{
              background:
                'linear-gradient(180deg,rgba(43,98,255,0),rgba(43,98,255,.14) 62%,rgba(22,210,255,.5))',
              animation: 'sxScan 1.35s cubic-bezier(.4,0,.2,1) 1.5s backwards',
            }}
          />
        </div>

        <WidgetComposer />
      </div>
    </div>
  );
}
